import {
  ContentType,
  IGetReactions,
  IReaction,
  ReactionType,
} from '@/models/Reaction'
import { GAEventTypes, trackCustomGAEvents } from '@ghl/clientportal-core'

import { updatePostDataInStates } from '@/helper/posts.helper'
import { IComments } from '@/models/Comments'
import { IPosts } from '@/models/Posts'
import { ReactionsService } from '@/service/ReactionsService'
import { PaginationParams } from '@/types/PaginationParams'
import { reactive } from 'vue'
import { CommentState } from './comments'
import { GroupState } from './groups'

interface IPreviousReactionState {
  contentId: string | undefined
  reactionId: string
  isLiked: boolean
}

export class ReactionsState {
  state = reactive({
    //isLoading holds the state of featching reactions from server, for a given reaction type on a given contentId
    isLoading: new Map<string, Map<ReactionType, boolean>>(), //map<contentId,map<reactionType,true|false>
    //contnentReactions holds the reactions for a given contentId and reactionType
    contentReactions: new Map<string, Map<ReactionType, IGetReactions[]>>(),
    //isNextPageAvaialble holds the state of whether next page/ new data is avaialble on server for a given contentId and reactionType
    isNextPageAvaialble: new Map<string, Map<ReactionType, boolean>>(), //map<contentId,map<reactionType,true|false>
    prevReactionStateMap: new Map<string, IPreviousReactionState | undefined>(),
  })

  private async getReactionsFromServer(
    contentId: string,
    reactionType: ReactionType,
    paginationObject?: PaginationParams
  ) {
    try {
      // when featching new data from server set isLoading for that contentId and reactionType to true
      const reactionTypeLoadingMap = new Map<ReactionType, boolean>()
      reactionTypeLoadingMap.set(reactionType, true)
      this.state.isLoading.set(contentId, reactionTypeLoadingMap)

      let newReactions: IGetReactions[] = []

      //fetch reactions from server
      newReactions = await ReactionsService.getReactions(
        contentId,
        reactionType,
        paginationObject
      )

      //Set content map if not available
      if (!this.state.contentReactions.has(contentId)) {
        const reactionTypeMap = new Map<ReactionType, IGetReactions[]>()
        reactionTypeMap.set(reactionType, newReactions)
        this.state.contentReactions.set(contentId, reactionTypeMap)
      } else {
        if (!this.state.contentReactions.get(contentId)?.has(reactionType)) {
          // if contentId is present in the map but reactionType is not present in the map, then set the reactionType map
          this.state.contentReactions
            .get(contentId)
            ?.set(reactionType, newReactions)
        } else {
          // if contentId and reactionType is present in the map, then add new reactions to the existing reactions
          const reactions = this.state.contentReactions
            .get(contentId)
            ?.get(reactionType) as IGetReactions[]
          reactions?.push(...newReactions)
          this.state.contentReactions
            .get(contentId)
            ?.set(reactionType, reactions)
        }
      }

      // set isLoading for that contentId and reactionType to false
      this.state.isLoading.get(contentId)?.set(reactionType, false)

      //check if we have reached the end of the list, if yes then set isNextPageAvailable for given contentId and reaction type to false, else do nothing
      if (
        paginationObject?.limit &&
        newReactions.length < paginationObject.limit
      ) {
        if (!this.state.isNextPageAvaialble.has(contentId)) {
          this.state.isNextPageAvaialble.set(
            contentId,
            new Map<ReactionType, boolean>()
          )
        }
        this.state.isNextPageAvaialble.get(contentId)?.set(reactionType, false)
      }
    } catch (error) {
      console.error(error)
      this.state.isNextPageAvaialble.get(contentId)?.set(reactionType, false)
    }
  }
  async fetchReactions(
    contentId: string,
    reactionType: ReactionType,
    paginationObject?: PaginationParams
  ): Promise<void> {
    // if next page is not available then do nothing
    if (
      this.state.isNextPageAvaialble.get(contentId)?.get(reactionType) === false
    ) {
      return
    }

    if (!paginationObject?.previousId) {
      if (
        this.state.contentReactions.has(contentId) &&
        this.state.contentReactions.get(contentId)?.has(reactionType)
      ) {
        //as no previousId is provided and we have reactions for given contentId and reactionType, no need to get new reactions from server
        return
      }
    } else {
      if (
        this.state.contentReactions.has(contentId) &&
        this.state.contentReactions.get(contentId)?.has(reactionType)
      ) {
        const previousIdIndex = this.state.contentReactions
          .get(contentId)
          ?.get(reactionType)
          ?.findIndex(reaction => reaction._id == paginationObject?.previousId)

        // check if the previousId provided is the Id of the last element of existing reactions, if yes then get new reactions from server, else do nothing
        if (
          previousIdIndex !=
          this.state.contentReactions.get(contentId)!.get(reactionType)!
            .length -
            1
        ) {
          return
        }
      }
    }

    // get reaction from server
    await this.getReactionsFromServer(contentId, reactionType, paginationObject)
  }

  async toggleReaction(
    reaction: IReaction,
    content: IPosts | IComments
  ): Promise<void | boolean> {
    let isAddReaction = true
    if (content?.current_user_reactions?.LIKE) {
      // if the user has already liked the post, then delete the like
      reaction['_id'] = content.current_user_reactions.reactionId
      isAddReaction = false
    }

    content = this.contentByType(reaction, content)
    if (!content) {
      return
    }

    this.checkAndUpdatePrevReactionState(content)
    this.updateContentReactionState(reaction, content, isAddReaction)

    return isAddReaction
  }

  private contentByType(reaction: IReaction, content: IPosts | IComments) {
    if (reaction.contentType == ContentType.POST) {
      content = content as IPosts
    } else if (reaction.contentType == ContentType.COMMENT) {
      content = CommentState.state.postComments
        .get(reaction.parentContentId as string)
        ?.find(comment => comment._id == reaction.contentId) as IComments
    }
    return content
  }

  private checkAndUpdatePrevReactionState(content: IPosts | IComments) {
    if (!this.state.prevReactionStateMap.get(content._id as string)) {
      this.state.prevReactionStateMap.set(content._id as string, {
        contentId: content._id,
        reactionId: content.current_user_reactions?.reactionId,
        isLiked: content.current_user_reactions?.LIKE,
      })
    }
  }

  private updateContentReactionState(
    reaction: IReaction,
    content: IPosts | IComments,
    isAddReaction: boolean
  ) {
    content.current_user_reactions = {
      ...content.current_user_reactions,
      [reaction.reactionType]: isAddReaction,
    }

    content.reactions = {
      ...content.reactions,
      [reaction.reactionType]:
        content.reactions[reaction.reactionType] + (isAddReaction ? 1 : -1),
    }

    if (reaction.contentType == ContentType.POST) {
      content = content as IPosts

      // when reaction is created store the reactionId to the post, homeTimelinePinnedPost and channelTimelinePinnedPost, which ever is not undefined
      updatePostDataInStates(
        content,
        GroupState.state.groupData.slug,
        content.channelSlug
      )
    }
  }

  async addOrDeleteReaction(
    reaction: IReaction,
    content: IPosts | IComments,
    isAddReaction: boolean
  ): Promise<any> {
    if (isAddReaction) {
      await this.addReaction(reaction, content, isAddReaction)
    } else {
      await this.deleteReaction(reaction, content, isAddReaction)
    }
  }

  private async addReaction(
    reaction: IReaction,
    content: IPosts | IComments,
    isAddReaction: boolean
  ): Promise<any> {
    const prevState = this.state.prevReactionStateMap.get(content._id as string)
    if (prevState?.isLiked == isAddReaction) {
      this.state.prevReactionStateMap.set(content._id as string, undefined)
      return
    }
    const newReaction = await ReactionsService.create(reaction)

    if (content) {
      content.current_user_reactions.reactionId = newReaction._id
    }
    await this.addReactionToStateMap(reaction)

    trackCustomGAEvents(GAEventTypes.CREATE_REACTIONS, {
      contentType: reaction.contentType,
    })
    this.state.prevReactionStateMap.set(content._id as string, undefined)
    return newReaction
  }

  private async deleteReaction(
    reaction: IReaction,
    content: IPosts | IComments,
    isAddReaction: boolean
  ): Promise<any> {
    const prevState = this.state.prevReactionStateMap.get(content._id as string)
    if (prevState?.isLiked == isAddReaction) {
      this.state.prevReactionStateMap.set(content._id as string, undefined)
      return
    }
    const deletedReaction = await ReactionsService.deleteReaction(
      reaction.contentId,
      prevState?.reactionId as string
    )
    this.deleteReactionFromStateMap(reaction, deletedReaction)

    if (content) {
      delete content.current_user_reactions.reactionId
    }

    trackCustomGAEvents(GAEventTypes.DELETE_REACTIONS, {
      contentType: reaction.contentType,
    })
    this.state.prevReactionStateMap.set(content._id as string, undefined)
    return deletedReaction
  }

  private async addReactionToStateMap(reaction: IReaction) {
    //if the contentId is not present in the map, then reactions for that contentId is not fetched yet, so no need to add new reaction to the map
    if (this.state.contentReactions.has(reaction.contentId)) {
      if (
        this.state.contentReactions
          .get(reaction.contentId)
          ?.has(reaction.reactionType)
      ) {
        this.state.contentReactions
          .get(reaction.contentId)
          ?.delete(reaction.reactionType)

        // if previously the content reaction was marked as next page not avaialble, when you create a new reaction, then we need to set is back to new page avaialble
        if (
          this.state.isNextPageAvaialble
            .get(reaction.contentId)
            ?.has(reaction.reactionType)
        ) {
          this.state.isNextPageAvaialble
            .get(reaction.contentId)
            ?.set(reaction.reactionType, true)
        }
        //get latest reactions from server
        await this.getReactionsFromServer(
          reaction.contentId,
          reaction.reactionType
        )
      }
    }
  }

  private deleteReactionFromStateMap(reaction: IReaction, deletedReaction) {
    // only delete the reaction from the map if it is present in the map
    if (this.state.contentReactions.has(reaction.contentId)) {
      const reactions = this.state.contentReactions.get(reaction.contentId)
      if (reactions?.has(deletedReaction.reactionType)) {
        const reactionList = reactions.get(deletedReaction.reactionType)
        if (reactionList) {
          const index = reactionList?.findIndex(
            reaction => reaction._id == deletedReaction._id
          )
          if (index != -1) {
            reactionList.splice(index, 1)
            this.state.contentReactions
              .get(reaction.contentId)
              ?.set(deletedReaction.reactionType, reactionList)
          }
        }
      }
    }
  }
}
export const ReactionState = new ReactionsState()
